import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Lock,
  Bell,
  Palette,
  Eye,
  EyeOff,
  Save,
  Loader2,
  Moon,
  Sun,
  Monitor,
  LogOut,
  ChevronRight,
  Camera,
  Trash2,
} from "lucide-react";
import { userService } from "../../api/services/userService";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfile, logout } from "../../store/slices/authSlice";
import { useToast } from "../../components/common/ThemeToaster";

const UserSettings = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState("profile");
  const [isLoading, setIsLoading] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profilePictureBase64, setProfilePictureBase64] = useState(null);
  const [profilePictureAlt, setProfilePictureAlt] = useState("");
  const fileInputRef = React.useRef(null);

  // Profile Settings
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    alternativePhone: "",
    company: "",
    position: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  });

  // Password Settings
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    orderUpdates: true,
    requestUpdates: true,
    productUpdates: false,
    marketingEmails: false,
    newsletter: true,
    systemAlerts: true,
  });

  // Appearance Settings
  const [appearanceSettings, setAppearanceSettings] = useState({
    theme: "system",
    language: "en",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "12h",
    compactView: false,
    animations: true,
    fontSize: "medium",
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        alternativePhone: user.alternativePhone || "",
        company: user.company || "",
        position: user.position || "",
        address: user.address || "",
        city: user.city || "",
        state: user.state || "",
        zipCode: user.zipCode || "",
        country: user.country || "",
      });

      if (user.profilePictureBase64) {
        setProfilePictureBase64(user.profilePictureBase64);
      }
      if (user.profilePictureAlt) {
        setProfilePictureAlt(user.profilePictureAlt);
      }
    }

    const savedTheme = localStorage.getItem("theme");
    const savedLanguage = localStorage.getItem("language");
    if (savedTheme)
      setAppearanceSettings((prev) => ({ ...prev, theme: savedTheme }));
    if (savedLanguage)
      setAppearanceSettings((prev) => ({ ...prev, language: savedLanguage }));
  }, [user]);

  // Helper function to format phone number
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `+1${cleaned}`;
    } else if (cleaned.length > 10) {
      return `+${cleaned}`;
    }
    return value;
  };

  // Helper function to validate phone number
  const isValidPhone = (phone) => {
    if (!phone) return true;
    const phoneRegex = /^\+\d{10,15}$/;
    return phoneRegex.test(phone);
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      toastError("Only image files are allowed (JPEG, PNG, GIF, WEBP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toastError("Image size must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      const response = await userService.updateProfilePicture(file);
      const updatedUser = response?.user || response?.data?.user || response;

      if (updatedUser?.profilePictureBase64) {
        setProfilePictureBase64(updatedUser.profilePictureBase64);
        await dispatch(getUserProfile());
        toastSuccess("Profile picture updated successfully!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toastError(
        error.response?.data?.message || "Failed to upload profile picture",
      );
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (
      !window.confirm("Are you sure you want to remove your profile picture?")
    ) {
      return;
    }

    setUploadingImage(true);
    try {
      const response = await userService.removeProfilePicture();
      const updatedUser = response?.user || response?.data?.user || response;

      setProfilePictureBase64(null);
      await dispatch(getUserProfile());
      toastSuccess("Profile picture removed successfully!");
    } catch (error) {
      console.error("Remove error:", error);
      toastError(
        error.response?.data?.message || "Failed to remove profile picture",
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFieldErrors({});

    // Format phone numbers before submitting
    const formattedPhone = profileData.phone
      ? formatPhoneNumber(profileData.phone)
      : undefined;
    const formattedAlternativePhone = profileData.alternativePhone
      ? formatPhoneNumber(profileData.alternativePhone)
      : undefined;

    // Validate phone numbers
    if (formattedPhone && !isValidPhone(formattedPhone)) {
      toastError("Phone number must be in E.164 format (e.g., +1234567890)");
      setIsLoading(false);
      return;
    }

    if (formattedAlternativePhone && !isValidPhone(formattedAlternativePhone)) {
      toastError(
        "Alternative phone number must be in E.164 format (e.g., +1234567890)",
      );
      setIsLoading(false);
      return;
    }

    try {
      const updateData = {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phone: formattedPhone,
        alternativePhone: formattedAlternativePhone,
        company: profileData.company || undefined,
        position: profileData.position || undefined,
        address: profileData.address || undefined,
        city: profileData.city || undefined,
        state: profileData.state || undefined,
        zipCode: profileData.zipCode || undefined,
        country: profileData.country || undefined,
        profilePictureAlt: profilePictureAlt || undefined,
      };

      console.log("Updating profile with data:", updateData);

      await userService.updateProfile(updateData);
      await dispatch(getUserProfile());
      toastSuccess("Profile updated successfully!");
    } catch (error) {
      console.error("Update profile error:", error);

      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const fieldErrorMap = {};
        errors.forEach((err) => {
          fieldErrorMap[err.path] = err.msg;
        });
        setFieldErrors(fieldErrorMap);
        toastError("Please check the form for errors");
      } else {
        toastError(error.response?.data?.message || "Failed to update profile");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    setProfileData({ ...profileData, phone: e.target.value });
  };

  const handleAlternativePhoneChange = (e) => {
    setProfileData({ ...profileData, alternativePhone: e.target.value });
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toastError("New passwords do not match");
      setIsLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toastError("Password must be at least 8 characters");
      setIsLoading(false);
      return;
    }

    try {
      await userService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      toastSuccess("Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Change password error:", error);

      let errorMessage = "Failed to change password";

      if (error.response?.data) {
        const responseData = error.response.data;

        if (typeof responseData === "string") {
          errorMessage = responseData;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        } else if (responseData.errors && Array.isArray(responseData.errors)) {
          const firstError = responseData.errors[0];
          errorMessage = firstError.msg || firstError.message || errorMessage;
        } else if (responseData.data?.message) {
          errorMessage = responseData.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (
        errorMessage === "Current password is incorrect" ||
        errorMessage.includes("Current password")
      ) {
        toastError("Current password is incorrect. Please try again.");
        setPasswordData((prev) => ({ ...prev, currentPassword: "" }));
      } else if (
        errorMessage.includes("Password must be at least 8 characters")
      ) {
        toastError("Password must be at least 8 characters");
        setPasswordData((prev) => ({
          ...prev,
          newPassword: "",
          confirmPassword: "",
        }));
      } else if (errorMessage.includes("New password must be different")) {
        toastError("New password must be different from your current password");
        setPasswordData((prev) => ({
          ...prev,
          newPassword: "",
          confirmPassword: "",
        }));
      } else {
        toastError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationUpdate = async () => {
    setIsLoading(true);
    try {
      localStorage.setItem(
        "notificationSettings",
        JSON.stringify(notificationSettings),
      );
      toastSuccess("Notification settings updated!");
    } catch (error) {
      toastError("Failed to update settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppearanceUpdate = async () => {
    setIsLoading(true);
    try {
      localStorage.setItem("theme", appearanceSettings.theme);
      localStorage.setItem("language", appearanceSettings.language);
      localStorage.setItem(
        "appearanceSettings",
        JSON.stringify(appearanceSettings),
      );

      if (appearanceSettings.theme === "dark") {
        document.documentElement.classList.add("dark");
      } else if (appearanceSettings.theme === "light") {
        document.documentElement.classList.remove("dark");
      } else {
        const isDark = window.matchMedia(
          "(prefers-color-scheme: dark)",
        ).matches;
        if (isDark) {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      }

      toastSuccess("Appearance settings updated!");
    } catch (error) {
      toastError("Failed to update settings");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    window.location.href = "/";
  };

  const tabs = [
    {
      id: "profile",
      label: "Profile",
      icon: User,
      description: "Manage your personal information",
    },
    {
      id: "password",
      label: "Password",
      icon: Lock,
      description: "Update your password",
    },
    {
      id: "notifications",
      label: "Notifications",
      icon: Bell,
      description: "Configure notification preferences",
    },
    {
      id: "appearance",
      label: "Appearance",
      icon: Palette,
      description: "Customize your interface",
    },
  ];

  const ToggleSwitch = ({ label, description, checked, onChange }) => (
    <div className="flex flex-col sm:flex-row sm:items-start justify-between py-3 gap-3">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </p>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 flex-shrink-0 ${
          checked ? "bg-primary-600" : "bg-gray-200 dark:bg-gray-700"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );

  const ThemeOption = ({ icon: Icon, label, theme }) => (
    <button
      onClick={() => setAppearanceSettings({ ...appearanceSettings, theme })}
      className={`flex-1 p-4 rounded-xl border-2 transition-all ${
        appearanceSettings.theme === theme
          ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
      }`}
    >
      <Icon
        size={24}
        className={`mx-auto mb-2 ${appearanceSettings.theme === theme ? "text-primary-600" : "text-gray-400"}`}
      />
      <p
        className={`text-sm font-medium text-center ${appearanceSettings.theme === theme ? "text-primary-600" : "text-gray-600 dark:text-gray-400"}`}
      >
        {label}
      </p>
    </button>
  );

  const renderProfileSettings = () => (
    <form onSubmit={handleProfileUpdate} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            First Name
          </label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) =>
              setProfileData({ ...profileData, firstName: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Last Name
          </label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) =>
              setProfileData({ ...profileData, lastName: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Email Address
          </label>
          <input
            type="email"
            value={user?.email || ""}
            disabled
            className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl cursor-not-allowed opacity-75"
          />
          <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Phone Number
          </label>
          <input
            type="tel"
            value={profileData.phone}
            onChange={handlePhoneChange}
            className={`w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all ${
              fieldErrors.phone
                ? "border-red-500"
                : "border-gray-200 dark:border-gray-700"
            }`}
            placeholder="+1234567890"
          />
          {fieldErrors.phone && (
            <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Format: +1234567890 (E.164 format)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Alternative Phone
          </label>
          <input
            type="tel"
            value={profileData.alternativePhone}
            onChange={handleAlternativePhoneChange}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            placeholder="+19876543210"
          />
          <p className="text-xs text-gray-500 mt-1">
            Format: +1234567890 (optional)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Company
          </label>
          <input
            type="text"
            value={profileData.company}
            onChange={(e) =>
              setProfileData({ ...profileData, company: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            placeholder="Your Company Inc."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Position
          </label>
          <input
            type="text"
            value={profileData.position}
            onChange={(e) =>
              setProfileData({ ...profileData, position: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            placeholder="Purchasing Manager"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Address
          </label>
          <input
            type="text"
            value={profileData.address}
            onChange={(e) =>
              setProfileData({ ...profileData, address: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            placeholder="Street address"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            City
          </label>
          <input
            type="text"
            value={profileData.city}
            onChange={(e) =>
              setProfileData({ ...profileData, city: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            State
          </label>
          <input
            type="text"
            value={profileData.state}
            onChange={(e) =>
              setProfileData({ ...profileData, state: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            ZIP Code
          </label>
          <input
            type="text"
            value={profileData.zipCode}
            onChange={(e) =>
              setProfileData({ ...profileData, zipCode: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Country
          </label>
          <input
            type="text"
            value={profileData.country}
            onChange={(e) =>
              setProfileData({ ...profileData, country: e.target.value })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            placeholder="United States"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Profile Picture Alt Text
          </label>
          <input
            type="text"
            value={profilePictureAlt}
            onChange={(e) => setProfilePictureAlt(e.target.value)}
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            placeholder="Profile picture description for accessibility"
          />
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-gray-800">
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-medium rounded-xl transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-primary-600/20"
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          {isLoading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );

  const renderPasswordSettings = () => (
    <form onSubmit={handlePasswordUpdate} className="space-y-5 max-w-lg">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Current Password
        </label>
        <div className="relative">
          <Lock
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type={showPassword ? "text" : "password"}
            value={passwordData.currentPassword}
            onChange={(e) =>
              setPasswordData({
                ...passwordData,
                currentPassword: e.target.value,
              })
            }
            className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          New Password
        </label>
        <div className="relative">
          <Lock
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type={showNewPassword ? "text" : "password"}
            value={passwordData.newPassword}
            onChange={(e) =>
              setPasswordData({ ...passwordData, newPassword: e.target.value })
            }
            className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            required
          />
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1.5">
          Password must be at least 8 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
          Confirm New Password
        </label>
        <div className="relative">
          <Lock
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={passwordData.confirmPassword}
            onChange={(e) =>
              setPasswordData({
                ...passwordData,
                confirmPassword: e.target.value,
              })
            }
            className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {passwordData.newPassword &&
          passwordData.confirmPassword &&
          passwordData.newPassword !== passwordData.confirmPassword && (
            <p className="mt-1.5 text-xs text-red-500">
              Passwords do not match
            </p>
          )}
      </div>

      <div className="flex justify-end pt-4">
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-medium rounded-xl transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-primary-600/20"
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          {isLoading ? "Updating..." : "Update Password"}
        </button>
      </div>
    </form>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Email Notifications
        </h3>
        <ToggleSwitch
          label="Email Notifications"
          description="Receive notifications via email"
          checked={notificationSettings.emailNotifications}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              emailNotifications: checked,
            })
          }
        />
        <ToggleSwitch
          label="Push Notifications"
          description="Receive notifications on your browser"
          checked={notificationSettings.pushNotifications}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              pushNotifications: checked,
            })
          }
        />
      </div>

      <div className="space-y-2 pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Event Notifications
        </h3>
        <ToggleSwitch
          label="Order Updates"
          description="Get notified about order status changes"
          checked={notificationSettings.orderUpdates}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              orderUpdates: checked,
            })
          }
        />
        <ToggleSwitch
          label="Request Updates"
          description="Get notified about request status changes"
          checked={notificationSettings.requestUpdates}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              requestUpdates: checked,
            })
          }
        />
        <ToggleSwitch
          label="Product Updates"
          description="Get notified about new products and offers"
          checked={notificationSettings.productUpdates}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              productUpdates: checked,
            })
          }
        />
        <ToggleSwitch
          label="System Alerts"
          description="Get notified about system maintenance"
          checked={notificationSettings.systemAlerts}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              systemAlerts: checked,
            })
          }
        />
      </div>

      <div className="space-y-2 pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Marketing
        </h3>
        <ToggleSwitch
          label="Marketing Emails"
          description="Receive promotional offers and updates"
          checked={notificationSettings.marketingEmails}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              marketingEmails: checked,
            })
          }
        />
        <ToggleSwitch
          label="Newsletter"
          description="Receive weekly industry insights"
          checked={notificationSettings.newsletter}
          onChange={(checked) =>
            setNotificationSettings({
              ...notificationSettings,
              newsletter: checked,
            })
          }
        />
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={handleNotificationUpdate}
          disabled={isLoading}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-medium rounded-xl transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-primary-600/20"
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          {isLoading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          Theme
        </h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <ThemeOption icon={Sun} label="Light" theme="light" />
          <ThemeOption icon={Moon} label="Dark" theme="dark" />
          <ThemeOption icon={Monitor} label="System" theme="system" />
        </div>
      </div>

      <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          Display Preferences
        </h3>
        <ToggleSwitch
          label="Compact View"
          description="Show more content with reduced spacing"
          checked={appearanceSettings.compactView}
          onChange={(checked) =>
            setAppearanceSettings({
              ...appearanceSettings,
              compactView: checked,
            })
          }
        />
        <ToggleSwitch
          label="Enable Animations"
          description="Show smooth transitions and animations"
          checked={appearanceSettings.animations}
          onChange={(checked) =>
            setAppearanceSettings({
              ...appearanceSettings,
              animations: checked,
            })
          }
        />

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Font Size
          </label>
          <select
            value={appearanceSettings.fontSize}
            onChange={(e) =>
              setAppearanceSettings({
                ...appearanceSettings,
                fontSize: e.target.value,
              })
            }
            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button
          onClick={handleAppearanceUpdate}
          disabled={isLoading}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-medium rounded-xl transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-primary-600/20"
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Save size={18} />
          )}
          {isLoading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );

  const CurrentTabIcon = tabs.find((t) => t.id === activeTab)?.icon;

  return (
    <div className="space-y-5 max-w-6xl mx-auto px-4 sm:px-0">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
          Settings
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Manage your account preferences and settings
        </p>
      </div>

      {/* Mobile Tab Selector */}
      <div className="lg:hidden">
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="w-full flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm"
        >
          <div className="flex items-center gap-3">
            {CurrentTabIcon && (
              <CurrentTabIcon size={18} className="text-primary-600" />
            )}
            <span className="font-medium text-gray-900 dark:text-white">
              {tabs.find((t) => t.id === activeTab)?.label}
            </span>
          </div>
          <ChevronRight
            size={18}
            className={`transition-transform ${showMobileMenu ? "rotate-90" : ""}`}
          />
        </button>

        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden mt-2"
            >
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setShowMobileMenu(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                        isActive
                          ? "bg-primary-50 dark:bg-primary-900/20 text-primary-600"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      <Icon size={18} />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop Layout */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Navigation - Desktop */}
        <div className="hidden lg:block lg:w-64 flex-shrink-0">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden sticky top-6">
            <nav className="p-2 space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? "bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400"
                        : "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                    }`}
                  >
                    <Icon size={18} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 sm:p-6">
            {activeTab === "profile" && renderProfileSettings()}
            {activeTab === "password" && renderPasswordSettings()}
            {activeTab === "notifications" && renderNotificationSettings()}
            {activeTab === "appearance" && renderAppearanceSettings()}
          </div>
        </div>
      </div>

      {/* Logout Section */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Logout from Account
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Sign out of your current session
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-all active:scale-95 shadow-md"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;
